import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { showAlert } from '@/utils/common/alert';

import { useAppDispatch, useAppSelector } from '@/store';
import {
  fetchWifiInfo,
  connectToWifi,
  disconnectFromWifi,
  clearError,
  setConnectionStatus,
  clearWifiInfo,
} from '@/store/slices/residents/wifiSlice';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { REDESIGN_SPACING, REDESIGN_TYPOGRAPHY } from '@/constants/residents/homeConstants';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';
import { strings } from '@/constants/residents/strings';
import WifiService from '@/services/residents/api/wifi/WifiService';
import { WifiConnectionStatus, WifiSupportType } from '@/types/residents/wifi';
import { HomeAPI } from '@/services/residents/api/HomeAPI';
import { Lease } from '@/types/residents/lease';
import { isWifiEligible } from '@/utils/residents/wifi/eligibility';
import { DEFAULT_WIFI_SUPPORT_PHONE } from '@/constants/residents/shared/wifi';
import { buildSupportOptions } from '@/utils/residents/wifi/support';

import { GradientBackground, GlassCard, PageHeader } from '@/components/residents/redesign';
import {
  WifiInfoGlassCard,
  WifiSupportGlassCard,
  WifiConnectionRow,
  WifiQRCodeModal,
} from '@/components/residents/redesign/wifi';
import { BaseResidentMobileScreen } from '@/components/residents/mobile/BaseResidentMobileScreen';
import { ScreenBackLink } from '@/components/residents/mobile/ScreenBackLink';
import { WifiSupportRow } from '@/components';

const HP = REDESIGN_SPACING.horizontalPadding;

export const WifiConnectionScreen: React.FC = () => {
  const { colors } = useThemeColors();
  const dispatch = useAppDispatch();
  const insets = useSafeAreaInsets();
  const { user } = useAppSelector((state) => state.auth);
  const { wifiInfo, connectionStatus, isLoading, error } = useAppSelector((state) => state.wifi);
  const [showQRCode, setShowQRCode] = useState(false);

  const prevLeaseId = useRef<number | undefined>(undefined);

  useEffect(() => {
    let isMounted = true;
    if (prevLeaseId.current !== user?.currentLeaseId) {
      dispatch(clearWifiInfo());
      prevLeaseId.current = user?.currentLeaseId;
    }
    dispatch(clearError());

    const init = async () => {
      if (!user?.currentLeaseId) return;
      try {
        const lease = await HomeAPI.getCurrentLease().catch(() => null) as (Partial<Lease> & { subStatus?: string }) | null;
        if (!isMounted) return;
        if (lease?.startDate && lease?.endDate) {
          if (isWifiEligible(lease)) {
            if (!isMounted) return;
            dispatch(fetchWifiInfo(user.currentLeaseId));
          }
        }
      } catch {
        if (!isMounted) return;
        dispatch(fetchWifiInfo(user.currentLeaseId));
      }
    };

    if (user?.currentLeaseId) {
      init();
    }

    const unsubscribe = WifiService.addConnectionStatusListener((status) => {
      if (!isMounted) return;
      dispatch(setConnectionStatus(status));
    });

    return () => {
      isMounted = false;
      unsubscribe();
      dispatch(clearWifiInfo());
    };
  }, [dispatch, user?.currentLeaseId]);

  const handleWifiButtonPress = useCallback(async () => {
    if (!wifiInfo) {
      showAlert('Error', 'No WiFi information available');
      return;
    }
    try {
      if (connectionStatus === WifiConnectionStatus.CONNECTED) {
        dispatch(disconnectFromWifi());
      } else {
        dispatch(connectToWifi());
      }
    } catch (err) {
      console.error('WiFi button press error:', err);
    }
  }, [wifiInfo, connectionStatus, dispatch]);

  const handleSupportPress = useCallback(async (type: WifiSupportType, value: string) => {
    try {
      switch (type) {
        case WifiSupportType.TEXT:
          await WifiService.openSupportText(value);
          break;
        case WifiSupportType.EMAIL:
          await WifiService.openSupportEmail(value);
          break;
        case WifiSupportType.WEBSITE:
          await WifiService.openSupportWebsite(value);
          break;
      }
    } catch {
      showAlert('Error', `Could not open ${type} app`);
    }
  }, []);

  const supportOptions = buildSupportOptions(wifiInfo || undefined);
  const qrCodeData = wifiInfo ? WifiService.generateQRCodeData(wifiInfo) : '';
  const propertyName = user?.property?.marketingName;

  // Error state
  if (error) {
    return (
      <GradientBackground>
        <BaseResidentMobileScreen screenName="WiFi" darkBackground headerVariant="gradient">
          <View style={styles.centeredContainer}>
            <Text style={[styles.errorText, { color: colors.accent.red }]}>{error}</Text>
            <TouchableOpacity
              style={styles.retryButton}
              onPress={() => {
                if (user?.currentLeaseId) {
                  dispatch(fetchWifiInfo(user.currentLeaseId));
                }
              }}
            >
              <Text style={styles.retryButtonText}>Retry</Text>
            </TouchableOpacity>
          </View>
        </BaseResidentMobileScreen>
      </GradientBackground>
    );
  }

  // No info state
  if (!wifiInfo && !isLoading) {
    return (
      <GradientBackground>
        <BaseResidentMobileScreen screenName="WiFi" darkBackground headerVariant="gradient">
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={[styles.scrollContent, { paddingBottom: 40 + insets.bottom }]}
            showsVerticalScrollIndicator={false}
          >
            <ScreenBackLink />
            <PageHeader title={strings.wifi.title} subtitle="Connection info & support" />
            <GlassCard>
              <Text style={[styles.noInfoTitle, { color: colors.text.primary }]}>
                {strings.wifi.support.title}
              </Text>
              <Text style={[styles.noInfoDescription, { color: colors.text.muted }]}>
                {strings.wifi.noInfo.subtitle}
              </Text>
              <WifiSupportRow
                option={{
                  type: WifiSupportType.TEXT,
                  title: strings.wifi.support.text,
                  value: DEFAULT_WIFI_SUPPORT_PHONE,
                  icon: 'chatbubble-outline',
                  urlPrefix: 'sms:',
                }}
                onPress={handleSupportPress}
              />
            </GlassCard>
          </ScrollView>
        </BaseResidentMobileScreen>
      </GradientBackground>
    );
  }

  // Loading state
  if (isLoading && !wifiInfo) {
    return (
      <GradientBackground>
        <BaseResidentMobileScreen screenName="WiFi" darkBackground headerVariant="gradient">
          <ScrollView
            style={styles.scrollView}
            contentContainerStyle={[styles.scrollContent, { paddingBottom: 40 + insets.bottom }]}
            showsVerticalScrollIndicator={false}
          >
            <ScreenBackLink />
            <PageHeader title={strings.wifi.title} subtitle="Connection info & support" />
            <View style={styles.centeredContainer}>
              <ActivityIndicator size="large" color={colors.text.primary} />
            </View>
          </ScrollView>
        </BaseResidentMobileScreen>
      </GradientBackground>
    );
  }

  // Main state
  return (
    <GradientBackground>
      <BaseResidentMobileScreen screenName="WiFi" darkBackground headerVariant="gradient">
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={[styles.scrollContent, { paddingBottom: 40 + insets.bottom }]}
          showsVerticalScrollIndicator={false}
        >
          <ScreenBackLink />
          <PageHeader title={strings.wifi.title} subtitle="Connection info & support" />

          {wifiInfo && (
            <WifiInfoGlassCard
              wifiInfo={wifiInfo}
              propertyName={propertyName}
              leaseUnit={wifiInfo.unit ? `Unit ${wifiInfo.unit}` : undefined}
              onPressQRCode={() => setShowQRCode(true)}
            />
          )}

          <WifiConnectionRow
            status={connectionStatus}
            onPress={handleWifiButtonPress}
            isLoading={isLoading}
          />

          {wifiInfo && supportOptions.length > 0 && (
            <WifiSupportGlassCard options={supportOptions} onPress={handleSupportPress} />
          )}
        </ScrollView>

        <WifiQRCodeModal
          visible={!!showQRCode && !!wifiInfo}
          title={strings.wifi.qrCode.title}
          value={qrCodeData}
          onClose={() => setShowQRCode(false)}
        />
      </BaseResidentMobileScreen>
    </GradientBackground>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingHorizontal: HP,
  },
  centeredContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  errorText: {
    ...REDESIGN_TYPOGRAPHY.body,
    textAlign: 'center',
    marginBottom: 16,
  },
  retryButton: {
    backgroundColor: PRIMITIVE_COLORS.resioBlue,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 10,
  },
  retryButtonText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 14,
  },
  noInfoTitle: {
    ...REDESIGN_TYPOGRAPHY.sectionTitle,
    marginBottom: 8,
  },
  noInfoDescription: {
    ...REDESIGN_TYPOGRAPHY.body,
    marginBottom: 16,
    lineHeight: 20,
  },
});
